'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Card from '@enlighten/design-system/dist/Card';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import { warmGrey } from '@enlighten/design-system/dist/colors';
import Grid from '@enlighten/design-system/dist/Grid';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { SolarSupportFormData } from '@/app/api/solar-support/route';
import ArrowRightIcon from '@/assets/images/pages/solar-support/arrow-right-icon.svg';
import FormItem from '@/components/pages/common/FormItem';
import { useOnChangeMessage } from '@/contexts/layouts/MessageProvider';
import toPhoneNumFormat from '@/utils/format';

const DEFAULT_VALUES: SolarSupportFormData = {
	name: '',
	mobile: '',
	companyName: '',
	address: '',
	email: '',
	content: '',
};

const Form = () => {
	const [isLoading, setIsLoading] = React.useState(false);

	const { control, handleSubmit, reset } = useForm<SolarSupportFormData>({
		mode: 'onSubmit',
		defaultValues: DEFAULT_VALUES,
	});

	const onChangeMessage = useOnChangeMessage();

	const handleFormSubmit = async (data: SolarSupportFormData) => {
		try {
			setIsLoading(true);
			const response = await fetch('/api/solar-support', {
				method: 'POST',
				body: JSON.stringify(data),
			});

			if (response.ok) {
				onChangeMessage({
					type: 'success',
					message: '신청이 완료되었습니다.',
					duration: 4000,
				});
				reset(DEFAULT_VALUES);
			} else {
				onChangeMessage({
					type: 'error',
					message: '신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
					duration: 4000,
				});
			}
		} catch (error) {
			console.error(error);
			onChangeMessage({
				type: 'error',
				message: '신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
				duration: 4000,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Grid
			component="div"
			id="solar-support-form"
			container
			justifyContent="center"
			alignItems="center"
			bgcolor={{ xs: '#F9F6FF', md: 'transparent' }}
			width="100%"
			padding={{ xs: '60px 0', md: '100px 0 140px' }}
			sx={{ scrollMarginTop: '80px' }}
		>
			<Grid
				container
				direction="column"
				alignItems="center"
				width="100%"
				maxWidth={1440}
				paddingX={3}
				sx={{
					'& img': {
						width: '100%',
						height: 'auto',
					},
				}}
			>
				<Card
					variant="outlined"
					sx={{
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						width: '100%',
						maxWidth: 680,
						borderRadius: 4,
						borderWidth: { xs: 0, md: 1 },
						padding: { xs: '40px 20px', md: '60px 20px' },
						boxShadow: {
							xs: '0 0 15px 0 rgba(0, 0, 0, 0.1)',
							md: '0 4px 28px 0 rgba(0, 0, 0, 0.1)',
						},
					}}
				>
					<Typography fontSize={{ xs: 24, md: 40 }} fontWeight={700} textAlign="center" whiteSpace={{ xs: 'pre-line', md: 'normal' }} lineHeight={{ xs: '33px', md: '52px' }} marginBottom={5}>
						{'태양광 지원사업\n사전 신청서'}
					</Typography>
					<Stack display={{ xs: 'none', md: 'flex' }} gap={{ xs: 5, md: '20px' }} width="100%" maxWidth={560}>
						<Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 5, md: 2 }}>
							<Controller
								control={control}
								name="name"
								rules={{
									required: '성함을 입력해 주세요.',
								}}
								render={({ field: { value, onChange }, fieldState: { error } }) => (
									<FormItem
										fullWidth
										variant="outlined"
										label="성함"
										required
										placeholder="성함을 입력해 주세요."
										value={value || ''}
										onChange={onChange}
										error={Boolean(error)}
										helperText={error?.message}
										slotProps={{ input: { sx: { fontSize: 16 } } }}
									/>
								)}
							/>
							<Controller
								control={control}
								name="mobile"
								rules={{
									required: '연락처를 입력해 주세요.',
									pattern: {
										value: /^\d{3}-\d{3,4}-\d{4}$/,
										message: '연락처 형식이 올바르지 않습니다.',
									},
								}}
								render={({ field: { value, onChange }, fieldState: { error } }) => (
									<FormItem
										fullWidth
										variant="outlined"
										label="연락처"
										required
										placeholder="연락처를 입력해 주세요."
										value={value || ''}
										onKeyDown={(e) => {
											if (!/[0-9]/g.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
												e.preventDefault();
											}
										}}
										onChange={(e) => {
											onChange(toPhoneNumFormat(e.target.value.replace(/-/g, '')));
										}}
										error={Boolean(error)}
										helperText={error?.message}
										slotProps={{
											input: {
												sx: { fontSize: 16 },
												inputProps: { maxLength: 13 },
											},
										}}
									/>
								)}
							/>
						</Stack>
						<Controller
							control={control}
							name="companyName"
							rules={{
								required: '업체명을 입력해 주세요.',
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="outlined"
									label="업체명"
									required
									placeholder="업체명을 입력해 주세요."
									value={value || ''}
									onChange={onChange}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{ input: { sx: { fontSize: 16 } } }}
								/>
							)}
						/>
						<Controller
							control={control}
							name="address"
							rules={{
								required: '주소를 입력해 주세요.',
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="outlined"
									label="사업지 주소"
									required
									placeholder="주소를 입력해 주세요."
									value={value || ''}
									onChange={onChange}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{ input: { sx: { fontSize: 16 } } }}
								/>
							)}
						/>
						<Controller
							control={control}
							name="email"
							rules={{
								pattern: {
									value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
									message: '이메일 형식이 올바르지 않습니다.',
								},
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="outlined"
									label="이메일 주소"
									placeholder="이메일을 입력해 주세요."
									value={value || ''}
									onChange={onChange}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{ input: { sx: { fontSize: 16 } } }}
								/>
							)}
						/>
						<Controller
							control={control}
							name="content"
							rules={{
								required: '문의 내용을 입력해 주세요.',
								minLength: { value: 10, message: '최소 10자 이상 입력해 주세요.' },
								maxLength: { value: 1000, message: '최대 1,000자까지 입력 가능합니다.' },
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="outlined"
									label="요청사항"
									required
									multiline
									minRows={3}
									placeholder="관심 지원사업을 포함하여, 문의 내용을 입력해 주세요. (최소 10자이상)"
									value={value || ''}
									onChange={onChange}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												padding: 0,
											},
											inputProps: {
												maxLength: 1000,
											},
										},
									}}
								/>
							)}
						/>
					</Stack>
					<Stack display={{ xs: 'flex', md: 'none' }} gap={{ xs: 5, md: '20px' }} width="100%" maxWidth={560}>
						<Controller
							control={control}
							name="name"
							rules={{
								required: '성함을 입력해 주세요.',
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									fullWidth
									variant="standard"
									label="성함"
									required
									placeholder="성함을 입력해 주세요."
									value={value || ''}
									onChange={(e) => {
										onChange(e.target.value);
									}}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												paddingY: '10px',
												'.MuiInput-input': {
													padding: 0,
												},
											},
										},
									}}
									sx={{
										'.MuiFormHelperText-root': {
											fontSize: 12,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="mobile"
							rules={{
								required: '연락처를 입력해 주세요.',
								pattern: {
									value: /^\d{3}-\d{3,4}-\d{4}$/,
									message: '연락처 형식이 올바르지 않습니다.',
								},
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									fullWidth
									variant="standard"
									label="연락처"
									required
									placeholder="연락처를 입력해 주세요."
									value={value || ''}
									onKeyDown={(e) => {
										if (!/[0-9]/g.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
											e.preventDefault();
										}
									}}
									onChange={(e) => {
										onChange(toPhoneNumFormat(e.target.value.replace(/-/g, '')));
									}}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												paddingY: '10px',
												'.MuiInput-input': {
													padding: 0,
												},
											},
											inputProps: { maxLength: 13 },
										},
									}}
									sx={{
										'.MuiFormHelperText-root': {
											fontSize: 12,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="companyName"
							rules={{
								required: '업체명을 입력해 주세요.',
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									fullWidth
									variant="standard"
									label="업체명"
									required
									placeholder="업체명을 입력해 주세요."
									value={value || ''}
									onChange={(e) => {
										onChange(e.target.value);
									}}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												paddingY: '10px',
												'.MuiInput-input': {
													padding: 0,
												},
											},
										},
									}}
									sx={{
										'.MuiFormHelperText-root': {
											fontSize: 12,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="address"
							rules={{
								required: '주소를 입력해 주세요.',
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="standard"
									label="사업지 주소"
									required
									placeholder="주소를 입력해 주세요."
									value={value || ''}
									onChange={(e) => {
										onChange(e.target.value);
									}}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												paddingY: '10px',
												'.MuiInput-input': {
													padding: 0,
												},
											},
										},
									}}
									sx={{
										'.MuiFormHelperText-root': {
											fontSize: 12,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="email"
							rules={{
								pattern: {
									value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
									message: '이메일 형식이 올바르지 않습니다.',
								},
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<FormItem
									variant="standard"
									label="이메일 주소"
									placeholder="이메일을 입력해 주세요."
									value={value || ''}
									onChange={(e) => {
										onChange(e.target.value);
									}}
									error={Boolean(error)}
									helperText={error?.message}
									slotProps={{
										input: {
											sx: {
												fontSize: 16,
												paddingY: '10px',
												'.MuiInput-input': {
													padding: 0,
												},
											},
										},
									}}
									sx={{
										'.MuiFormHelperText-root': {
											fontSize: 12,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="content"
							rules={{
								required: '문의 내용을 입력해 주세요.',
								minLength: { value: 10, message: '최소 10자 이상 입력해 주세요.' },
								maxLength: { value: 1000, message: '최대 1,000자까지 입력 가능합니다.' },
							}}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<Stack gap="3px">
									<FormItem
										variant="outlined"
										label="요청사항"
										labelProps={{ marginBottom: 2 }}
										required
										multiline
										minRows={3}
										placeholder="관심 지원사업을 포함하여, 문의 내용을 입력해 주세요. (최소 10자이상)"
										value={value || ''}
										onChange={(e) => {
											onChange(e.target.value);
										}}
										error={Boolean(error)}
										slotProps={{
											input: {
												sx: {
													fontSize: 16,
													padding: 0,
												},
												inputProps: {
													maxLength: 1000,
												},
											},
										}}
									/>
									<Stack direction="row" justifyContent="space-between">
										<Typography fontSize={12} fontWeight={400} color="#E52F2F">
											{error ? error.message : ' '}
										</Typography>
										<Typography fontSize={12} fontWeight={400} color={warmGrey['700']}>
											<span style={{ color: 'black' }}>{(value || '').length.toLocaleString()}</span>
											{' / 최대 1,000자'}
										</Typography>
									</Stack>
								</Stack>
							)}
						/>
					</Stack>
					<Button
						fullWidth
						onClick={handleSubmit(handleFormSubmit)}
						endIcon={<ArrowRightIcon />}
						sx={{
							maxWidth: 560,
							height: { xs: '52px', md: '60px' },
							marginTop: { xs: '60px', md: '40px' },
							borderRadius: '6px',
							fontSize: { xs: 16, md: 18 },
							fontWeight: 700,

							'& .MuiButton-icon': {
								display: { xs: 'none', md: 'inherit' },
							},
						}}
					>
						상담 신청하기
					</Button>
					{isLoading ? (
						<Box position="absolute" display="flex" justifyContent="center" alignItems="center" bgcolor="common.white" width="100%" height="100%" sx={{ opacity: 0.8 }}>
							<CircularProgress />
						</Box>
					) : null}
				</Card>
			</Grid>
		</Grid>
	);
};

export default Form;
